import { useState, useMemo, useEffect, useRef, useCallback, lazy, Suspense, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'motion/react';
import {
  Search,
  FileText,
  Table2,
  FileDown,
  Layers,
  User,
  FolderArchive,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Loader2,
  Building2,
  Filter,
  LayoutGrid,
  Save,
  Star,
  Trash2,
  X,
  Check,
} from 'lucide-react';
import { BaseModal } from '@/components/modal/BaseModal';
import { MODAL_COLORS } from '@/components/modal/constants';
import useMediaQuery, { MD_BREAKPOINT } from '@/hooks/useMediaQuery';
import { ADMIN_COLORS } from '../utils/adminAnimations';
import { STATUS_BADGE_CONFIG, STATUS_LABEL_KEY } from './StatusBadge';
import type { ExportLayout } from '../utils/export/exportTypes';
import type { ExportColumnId, ExportGroupBy, ExportTemplate } from '../utils/export/exportColumns';
import {
  ALL_COLUMN_IDS,
  ALL_EXPORT_COLUMNS,
  EXPORT_TEMPLATES,
} from '../utils/export/exportColumns';
import type { ExportPreset, ExportPresetConfig } from '../types';
import {
  useExportPresets,
  useCreateExportPreset,
  useDeleteExportPreset,
  useSetExportPresetDefault,
} from '../hooks';

export interface WizardParams {
  employeeIds: Set<string>;
  statuses: Set<string>;
  reportTypes: Set<string>;
  aggregated: boolean;
  format: 'csv' | 'xlsx' | 'pdf';
  layout: ExportLayout;
  columns: ExportColumnId[];
  groupBy: ExportGroupBy;
}

export interface ExportWizardModalProps {
  isOpen: boolean;
  onClose: () => void;
  employees: { id: string; name: string; role: string }[];
  statuses: string[];
  /** Optional report types for filtering in step 2 */
  reportTypes?: { code: string; name: string }[];
  onExport: (params: WizardParams) => Promise<void>;
  isPending?: boolean;
  /** Override the modal title (default: export.wizard.title) */
  titleKey?: string;
  /** Override Step 1 heading (default: export.wizard.selectEmployees) */
  step1LabelKey?: string;
  /** Override Step 1 search placeholder (default: export.wizard.searchEmployees) */
  step1SearchKey?: string;
  /** Override "Per Employee" layout label (default: export.wizard.layoutPerEmployee) */
  layoutPerItemKey?: string;
  /** When provided, step 4 shows a live PDF preview */
  buildPreviewDocument?: (params: WizardParams) => ReactElement;
}

const LazyPDFViewer = lazy(() =>
  import('@react-pdf/renderer').then(mod => ({ default: mod.PDFViewer }))
);

const FORMAT_OPTIONS = [
  { value: 'csv' as const, icon: FileText, color: '#059669', label: 'export.wizard.formatCsv' },
  { value: 'xlsx' as const, icon: Table2, color: '#0284c7', label: 'export.wizard.formatXlsx' },
  { value: 'pdf' as const, icon: FileDown, color: '#dc2626', label: 'export.wizard.formatPdf' },
];

function getLayoutOptions(perItemLabel: string) {
  const isHospital = perItemLabel.includes('Hospital');
  return [
    { value: 'combined' as ExportLayout, icon: Layers, label: 'export.wizard.layoutCombined' },
    { value: 'per-employee' as ExportLayout, icon: isHospital ? Building2 : User, label: perItemLabel },
    { value: 'separate-files' as ExportLayout, icon: FolderArchive, label: 'export.wizard.layoutSeparateFiles' },
  ];
}

const TOTAL_STEPS = 4;
const EMPTY_REPORT_TYPES: { code: string; name: string }[] = [];

export function ExportWizardModal({
  isOpen,
  onClose,
  employees,
  statuses,
  reportTypes: reportTypesProp = EMPTY_REPORT_TYPES,
  onExport,
  isPending = false,
  titleKey = 'export.wizard.title',
  step1LabelKey = 'export.wizard.selectEmployees',
  step1SearchKey = 'export.wizard.searchEmployees',
  layoutPerItemKey = 'export.wizard.layoutPerEmployee',
  buildPreviewDocument,
}: ExportWizardModalProps) {
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery(MD_BREAKPOINT);
  const isRTL = i18n.language === 'he';

  // Context: hospitals vs employees — drives icon choice
  const isHospitalContext = useMemo(
    () => step1LabelKey.toLowerCase().includes('hospital'),
    [step1LabelKey],
  );
  const ItemIcon = isHospitalContext ? Building2 : User;

  const STATUS_ORDER = ['PAID', 'APPROVED', 'SUBMITTED', 'PENDING', 'REJECTED'] as const;
  const orderedStatuses = useMemo(
    () => [...statuses].sort((a, b) => STATUS_ORDER.indexOf(a as any) - STATUS_ORDER.indexOf(b as any)),
    [statuses],
  );

  // --- State ---
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [visitedSteps, setVisitedSteps] = useState<Set<number>>(() => new Set([1]));
  const [selectedEmployees, setSelectedEmployees] = useState<Set<string>>(() => new Set(employees.map(e => e.id)));
  const [selectedStatuses, setSelectedStatuses] = useState<Set<string>>(() => new Set(['PAID']));
  const [format, setFormat] = useState<'csv' | 'xlsx' | 'pdf'>('xlsx');
  const [layout, setLayout] = useState<ExportLayout>('combined');
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [selectedColumns, setSelectedColumns] = useState<ExportColumnId[]>([...ALL_COLUMN_IDS]);
  const [selectedTemplate, setSelectedTemplate] = useState<ExportTemplate | null>('detailed');
  const [selectedReportTypes, setSelectedReportTypes] = useState<Set<string>>(() => new Set(reportTypesProp.map(rt => rt.code)));
  const [aggregated, setAggregated] = useState(true);
  const groupBy: ExportGroupBy = 'hospital';
  const [columnsExpanded, setColumnsExpanded] = useState(false);

  const hasPreview = !!buildPreviewDocument;

  // Export Presets
  const { data: presets = [] } = useExportPresets();
  const createPreset = useCreateExportPreset();
  const deletePreset = useDeleteExportPreset();
  const setDefault = useSetExportPresetDefault();
  const [showSavePreset, setShowSavePreset] = useState(false);
  const [presetName, setPresetName] = useState('');
  const defaultAppliedRef = useRef(false);

  // Track visited steps for clickable step indicators
  useEffect(() => {
    setVisitedSteps(prev => {
      if (prev.has(step)) return prev;
      const next = new Set(prev);
      next.add(step);
      return next;
    });
  }, [step]);

  // Apply default preset when modal opens
  useEffect(() => {
    if (isOpen && presets.length > 0 && !defaultAppliedRef.current) {
      const defaultPreset = presets.find(p => p.isDefault);
      if (defaultPreset) applyPreset(defaultPreset);
      defaultAppliedRef.current = true;
    }
    if (!isOpen) defaultAppliedRef.current = false;
  }, [isOpen, presets]);

  // Reset state when modal closes
  const handleClose = () => {
    setStep(1);
    setDirection(1);
    setVisitedSteps(new Set([1]));
    setSelectedEmployees(new Set(employees.map(e => e.id)));
    setSelectedStatuses(new Set(orderedStatuses.includes('PAID') ? ['PAID'] : orderedStatuses));
    setFormat('xlsx');
    setLayout('combined');
    setSearch('');
    setError('');
    setSelectedColumns([...ALL_COLUMN_IDS]);
    setSelectedTemplate('detailed');
    setSelectedReportTypes(new Set(reportTypesProp.map(rt => rt.code)));
    setAggregated(true);
    setColumnsExpanded(false);
    setShowSavePreset(false);
    setPresetName('');
    onClose();
  };

  // Sync selections when parent data changes
  const employeeIdsKey = employees.map(e => e.id).join(',');
  const statusesKey = orderedStatuses.join(',');
  const reportTypeCodesKey = reportTypesProp.map(rt => rt.code).join(',');

  useEffect(() => {
    setSelectedEmployees(new Set(employees.map(e => e.id)));
  }, [employeeIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSelectedStatuses(new Set(orderedStatuses.includes('PAID') ? ['PAID'] : orderedStatuses));
  }, [statusesKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSelectedReportTypes(new Set(reportTypesProp.map(rt => rt.code)));
  }, [reportTypeCodesKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredEmployees = useMemo(() => {
    if (!search.trim()) return employees;
    const q = search.toLowerCase();
    return employees.filter(e => e.name.toLowerCase().includes(q) || e.role.toLowerCase().includes(q));
  }, [employees, search]);

  // --- Toggle helpers ---
  const toggleEmployee = (id: string) => {
    setSelectedEmployees(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleStatus = (s: string) => {
    setSelectedStatuses(prev => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s); else next.add(s);
      return next;
    });
  };

  const toggleAllEmployees = () => {
    setSelectedEmployees(
      selectedEmployees.size === employees.length ? new Set() : new Set(employees.map(e => e.id)),
    );
  };

  const toggleAllStatuses = () => {
    setSelectedStatuses(
      selectedStatuses.size === orderedStatuses.length ? new Set() : new Set(orderedStatuses),
    );
  };

  const toggleReportType = (code: string) => {
    setSelectedReportTypes(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  };

  const toggleAllReportTypes = () => {
    setSelectedReportTypes(
      selectedReportTypes.size === reportTypesProp.length
        ? new Set()
        : new Set(reportTypesProp.map(rt => rt.code)),
    );
  };

  const handleSelectTemplate = (tmpl: ExportTemplate) => {
    setSelectedTemplate(tmpl);
    setSelectedColumns([...EXPORT_TEMPLATES[tmpl].columns]);
  };

  const toggleColumn = (colId: ExportColumnId) => {
    setSelectedTemplate(null);
    setSelectedColumns(prev => {
      if (prev.includes(colId)) return prev.filter(c => c !== colId);
      return ALL_COLUMN_IDS.filter(c => prev.includes(c) || c === colId);
    });
  };

  // --- Presets ---
  function applyPreset(preset: ExportPreset) {
    const c = preset.config;
    if (c.statuses) setSelectedStatuses(new Set(c.statuses));
    if (c.reportTypes) setSelectedReportTypes(new Set(c.reportTypes));
    if (c.aggregated !== undefined) setAggregated(c.aggregated);
    if (c.columns) { setSelectedColumns(c.columns); setSelectedTemplate(null); }
    if (c.format) setFormat(c.format);
    if (c.layout) setLayout(c.layout);
  }

  async function handleSavePreset() {
    if (!presetName.trim()) return;
    const config: ExportPresetConfig = {
      statuses: [...selectedStatuses],
      reportTypes: [...selectedReportTypes],
      aggregated,
      columns: selectedColumns,
      format,
      layout,
      groupBy,
    };
    await createPreset.mutateAsync({ name: presetName.trim(), config });
    setShowSavePreset(false);
    setPresetName('');
  }

  async function handleDeletePreset(id: string) {
    if (!confirm(t('export.wizard.presetDeleteConfirm'))) return;
    await deletePreset.mutateAsync(id);
  }

  async function handleToggleDefault(preset: ExportPreset) {
    if (preset.isDefault) return;
    await setDefault.mutateAsync(preset.id);
  }

  // --- Wizard params ---
  const wizardParams: WizardParams = useMemo(() => ({
    employeeIds: selectedEmployees,
    statuses: selectedStatuses,
    reportTypes: selectedReportTypes,
    aggregated,
    format,
    layout,
    columns: selectedColumns,
    groupBy,
  }), [selectedEmployees, selectedStatuses, selectedReportTypes, aggregated, format, layout, selectedColumns, groupBy]);

  // --- Navigation ---
  const canNext = () => {
    if (step === 1 && selectedEmployees.size === 0) return false;
    if (step === 2 && selectedStatuses.size === 0) return false;
    if (step === 2 && reportTypesProp.length > 0 && selectedReportTypes.size === 0) return false;
    if (step === 3 && selectedColumns.length === 0) return false;
    return true;
  };

  const goToStep = useCallback((target: number) => {
    setError('');
    setDirection(target > step ? 1 : -1);
    setStep(target);
  }, [step]);

  const handleNext = () => {
    if (step === 1 && selectedEmployees.size === 0) {
      setError(t('export.wizard.noEmployeesSelected'));
      return;
    }
    if (step === 2 && selectedStatuses.size === 0) {
      setError(t('export.wizard.noStatusesSelected'));
      return;
    }
    setError('');
    setDirection(1);
    setStep(s => Math.min(s + 1, TOTAL_STEPS));
  };

  const handleBack = () => {
    setError('');
    setDirection(-1);
    setStep(s => Math.max(s - 1, 1));
  };

  const handleExport = async () => {
    await onExport(wizardParams);
    handleClose();
  };

  const BackIcon = isRTL ? ChevronRight : ChevronLeft;
  const NextIcon = isRTL ? ChevronLeft : ChevronRight;


  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      preventClose={isPending}
      mobileVariant="fullscreen"
      maxWidth="max-w-xl"
      ariaLabel={t(titleKey)}
    >
      {/* Drag handle (mobile) */}
      {isMobile && (
        <div className="flex justify-center pt-2 pb-1 flex-shrink-0">
          <div
            className="w-12 h-1.5 rounded-full"
            style={{ backgroundColor: MODAL_COLORS.border, opacity: 0.3 }}
          />
        </div>
      )}

      {/* Header */}
      <div
        className="flex items-center justify-center relative px-4 py-3 flex-shrink-0"
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.4)',
          ...(isMobile
            ? {
                backdropFilter: 'blur(40px) saturate(180%)',
                WebkitBackdropFilter: 'blur(40px) saturate(180%)',
                marginTop: 'env(safe-area-inset-top, 0px)',
              }
            : {}),
        }}
      >
        <h1
          style={{
            color: MODAL_COLORS.text,
            fontSize: 18,
            fontWeight: 700,
            letterSpacing: 0.5,
          }}
        >
          {t(titleKey)}
        </h1>
      </div>

      {/* Step indicators — clickable numbered circles */}
      <div className="flex items-center justify-center gap-0 px-4 py-3 flex-shrink-0">
        {Array.from({ length: TOTAL_STEPS }, (_, i) => {
          const stepNum = i + 1;
          const isActive = step === stepNum;
          const isCompleted = step > stepNum;
          const isVisited = visitedSteps.has(stepNum);
          const isClickable = isCompleted || (isVisited && stepNum !== step);

          return (
            <div key={i} className="flex items-center">
              {/* Connector line */}
              {i > 0 && (
                <div
                  className="transition-colors duration-300"
                  style={{
                    width: isMobile ? 16 : 28,
                    height: 1,
                    backgroundColor: isCompleted || isActive
                      ? 'var(--color-primary-300)'
                      : ADMIN_COLORS.border,
                  }}
                />
              )}

              {/* Step circle */}
              <button
                type="button"
                onClick={() => isClickable && goToStep(stepNum)}
                disabled={!isClickable}
                className="transition-all"
                style={{ cursor: isClickable ? 'pointer' : 'default' }}
              >
                <div
                  className="flex items-center justify-center rounded-full transition-all duration-300 text-xs font-bold"
                  style={{
                    width: isActive ? 36 : 30,
                    height: isActive ? 36 : 30,
                    backgroundColor: isActive
                      ? 'var(--color-primary-500)'
                      : isCompleted
                        ? ADMIN_COLORS.successBg
                        : 'rgba(241, 245, 249, 0.8)',
                    color: isActive
                      ? '#fff'
                      : isCompleted
                        ? ADMIN_COLORS.successText
                        : ADMIN_COLORS.textTertiary,
                    boxShadow: isActive
                      ? '0 2px 8px color-mix(in srgb, var(--color-primary-500) 30%, transparent)'
                      : 'none',
                  }}
                >
                  {isCompleted ? <Check className="w-3.5 h-3.5" /> : stepNum}
                </div>
              </button>
            </div>
          );
        })}
      </div>

      {/* Step title */}
      <div className="px-4 pb-2 flex-shrink-0">
        <h2 className="text-sm font-semibold" style={{ color: MODAL_COLORS.text }}>
          {step === 1 && t(step1LabelKey)}
          {step === 2 && t('export.wizard.filterData')}
          {step === 3 && t('export.wizard.customizeReport')}
          {step === 4 && (hasPreview ? t('export.wizard.preview') : t('export.wizard.selectFormat'))}
        </h2>
      </div>

      {/* Scrollable body — AnimatePresence renders only the active step */}
      <div
        className="flex-1 overflow-y-auto hide-scrollbar px-4 pb-2"
        style={{ overscrollBehaviorY: 'contain' }}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={step}
            initial={{ opacity: 0, x: direction * (isRTL ? -30 : 30) }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: direction * (isRTL ? 30 : -30) }}
            transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            {/* ───────── Step 1: Select Items ───────── */}
            {step === 1 && (
              <div className="space-y-2">
                {/* Search */}
                <div
                  className="flex items-center gap-2 rounded-xl px-3 py-2"
                  style={{
                    backgroundColor: 'rgba(241, 245, 249, 0.8)',
                    border: `1px solid ${ADMIN_COLORS.border}`,
                  }}
                >
                  <Search className="w-3.5 h-3.5 flex-shrink-0" style={{ color: ADMIN_COLORS.textTertiary }} />
                  <input
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder={t(step1SearchKey)}
                    className="flex-1 bg-transparent text-xs outline-none placeholder:text-slate-400"
                    style={{ color: ADMIN_COLORS.navy }}
                  />
                </div>

                {/* Select all + count */}
                <div className="flex items-center justify-between">
                  <button
                    onClick={toggleAllEmployees}
                    className="text-[11px] font-semibold transition-colors"
                    style={{ color: 'var(--color-primary-500)' }}
                  >
                    {selectedEmployees.size === employees.length
                      ? t('export.wizard.deselectAll')
                      : t('export.wizard.selectAll')}
                  </button>
                  <span className="text-[10px] font-medium" style={{ color: ADMIN_COLORS.textTertiary }}>
                    {selectedEmployees.size}/{employees.length}
                  </span>
                </div>

                {/* Employee grid */}
                <div className="grid grid-cols-2 gap-2 max-h-[300px] overflow-y-auto hide-scrollbar">
                  {filteredEmployees.map(emp => {
                    const selected = selectedEmployees.has(emp.id);
                    return (
                      <button
                        key={emp.id}
                        type="button"
                        onClick={() => toggleEmployee(emp.id)}
                        className="flex flex-col items-center gap-1.5 rounded-xl px-2 py-3 cursor-pointer transition-all"
                        style={{
                          backgroundColor: selected ? 'rgba(21, 101, 192, 0.08)' : 'rgba(241, 245, 249, 0.6)',
                          border: `2px solid ${selected ? 'var(--color-primary-400)' : 'transparent'}`,
                          boxShadow: selected ? '0 0 0 1px var(--color-primary-200)' : 'none',
                        }}
                      >
                        <div
                          className="w-9 h-9 rounded-full flex items-center justify-center transition-colors"
                          style={{
                            backgroundColor: selected ? 'var(--color-primary-500)' : 'rgba(148, 163, 184, 0.2)',
                          }}
                        >
                          <ItemIcon
                            className="w-4.5 h-4.5"
                            style={{ color: selected ? '#fff' : ADMIN_COLORS.textSecondary }}
                          />
                        </div>
                        <span
                          className="text-[11px] font-medium text-center leading-tight line-clamp-2"
                          style={{ color: selected ? 'var(--color-primary-700)' : ADMIN_COLORS.navy }}
                        >
                          {emp.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ───────── Step 2: Filter ───────── */}
            {step === 2 && (
              <div className="space-y-3">
                {/* Aggregated toggle */}
                <label
                  className="flex items-center gap-2 rounded-lg px-2.5 py-2 cursor-pointer transition-colors hover:bg-white/60"
                  style={{
                    backgroundColor: aggregated ? 'rgba(255,255,255,0.8)' : 'transparent',
                    border: `1px solid ${aggregated ? 'var(--color-primary-200)' : ADMIN_COLORS.border}`,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={aggregated}
                    onChange={() => setAggregated(prev => !prev)}
                    className="neo-checkbox"
                  />
                  <span className="text-[11px] font-semibold" style={{ color: ADMIN_COLORS.navy }}>
                    {t('export.wizard.aggregated')}
                  </span>
                </label>

                {/* Report type filter */}
                {reportTypesProp.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <span
                        className="text-[10px] font-semibold uppercase tracking-wider"
                        style={{ color: ADMIN_COLORS.textSecondary }}
                      >
                        {t('export.wizard.reportTypes')}
                      </span>
                      <button
                        onClick={toggleAllReportTypes}
                        className="text-[10px] font-semibold transition-colors"
                        style={{ color: 'var(--color-primary-500)' }}
                      >
                        {selectedReportTypes.size === reportTypesProp.length
                          ? t('export.wizard.deselectAll')
                          : t('export.wizard.selectAll')}
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-1">
                      {reportTypesProp.map(rt => {
                        const isActive = selectedReportTypes.has(rt.code);
                        return (
                          <label
                            key={rt.code}
                            className="flex items-center gap-2 rounded-lg px-2.5 py-2 cursor-pointer transition-colors hover:bg-white/60"
                            style={{
                              backgroundColor: isActive ? 'rgba(255,255,255,0.8)' : 'transparent',
                              border: `1px solid ${isActive ? 'var(--color-primary-200)' : 'transparent'}`,
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isActive}
                              onChange={() => toggleReportType(rt.code)}
                              className="neo-checkbox"
                            />
                            <span className="text-[11px] font-medium" style={{ color: ADMIN_COLORS.navy }}>
                              {rt.name}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Statuses — uses shared STATUS_BADGE_CONFIG colors */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span
                      className="text-[10px] font-semibold uppercase tracking-wider"
                      style={{ color: ADMIN_COLORS.textSecondary }}
                    >
                      {t('export.wizard.selectStatuses')}
                    </span>
                    <button
                      onClick={toggleAllStatuses}
                      className="text-[10px] font-semibold transition-colors"
                      style={{ color: 'var(--color-primary-500)' }}
                    >
                      {selectedStatuses.size === orderedStatuses.length
                        ? t('export.wizard.deselectAll')
                        : t('export.wizard.selectAll')}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    {orderedStatuses.map(s => {
                      const badge = STATUS_BADGE_CONFIG[s];
                      const bg = badge?.bg ?? 'rgba(241, 245, 249, 0.8)';
                      const fg = badge?.fg ?? '#475569';
                      const BadgeIcon = badge?.icon;
                      return (
                        <label
                          key={s}
                          className="flex items-center gap-2 rounded-lg px-2.5 py-2 cursor-pointer transition-colors hover:bg-white/60"
                          style={{
                            backgroundColor: selectedStatuses.has(s) ? 'rgba(255,255,255,0.8)' : 'transparent',
                            border: `1px solid ${selectedStatuses.has(s) ? 'var(--color-primary-200)' : 'transparent'}`,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedStatuses.has(s)}
                            onChange={() => toggleStatus(s)}
                            className="neo-checkbox"
                          />
                          <span
                            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold"
                            style={{ backgroundColor: bg, color: fg }}
                          >
                            {BadgeIcon && <BadgeIcon className="w-3 h-3" />}
                            {t(STATUS_LABEL_KEY[s] ?? `admin.status${s.charAt(0) + s.slice(1).toLowerCase()}`)}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* ───────── Step 3: Customize Report ───────── */}
            {step === 3 && (
              <div className="space-y-4">
                {/* Saved presets */}
                {presets.length > 0 && (
                  <div>
                    <div
                      className="text-[10px] font-semibold uppercase tracking-wider mb-2"
                      style={{ color: ADMIN_COLORS.textSecondary }}
                    >
                      {t('export.wizard.savedPresets')}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {presets.map(preset => (
                        <div key={preset.id} className="relative group">
                          <button
                            onClick={() => applyPreset(preset)}
                            className="rounded-full px-3 py-1.5 text-[11px] font-semibold transition-all hover:scale-[1.02] active:scale-[0.98]"
                            style={{
                              backgroundColor: preset.isDefault ? 'var(--color-primary-50)' : 'rgba(255,255,255,0.7)',
                              color: preset.isDefault ? 'var(--color-primary-700)' : ADMIN_COLORS.navy,
                              border: `1px solid ${preset.isDefault ? 'var(--color-primary-300)' : ADMIN_COLORS.border}`,
                            }}
                          >
                            {preset.isDefault && (
                              <Star
                                className="w-3 h-3 inline-block me-1 -mt-0.5"
                                style={{ fill: 'var(--color-primary-500)', color: 'var(--color-primary-500)' }}
                              />
                            )}
                            {preset.name}
                          </button>
                          {/* Hover action icons — group class on parent enables group-hover */}
                          <div className="absolute -top-1 -end-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            {!preset.isDefault && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleToggleDefault(preset); }}
                                className="w-5 h-5 rounded-full flex items-center justify-center bg-white shadow-sm border"
                                style={{ borderColor: ADMIN_COLORS.border }}
                                title={t('export.wizard.presetSetDefault')}
                              >
                                <Star className="w-2.5 h-2.5" style={{ color: ADMIN_COLORS.textSecondary }} />
                              </button>
                            )}
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDeletePreset(preset.id); }}
                              className="w-5 h-5 rounded-full flex items-center justify-center bg-white shadow-sm border"
                              style={{ borderColor: ADMIN_COLORS.border }}
                              title={t('export.wizard.presetDeleteConfirm')}
                            >
                              <Trash2 className="w-2.5 h-2.5" style={{ color: '#dc2626' }} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Template chips */}
                <div className="flex flex-wrap gap-2">
                  {(Object.keys(EXPORT_TEMPLATES) as ExportTemplate[]).map(tmpl => {
                    const isActive = selectedTemplate === tmpl;
                    return (
                      <button
                        key={tmpl}
                        onClick={() => handleSelectTemplate(tmpl)}
                        className="rounded-full px-3 py-1.5 text-[11px] font-semibold transition-all"
                        style={{
                          backgroundColor: isActive ? 'var(--color-primary-500)' : 'rgba(255,255,255,0.6)',
                          color: isActive ? '#fff' : ADMIN_COLORS.navy,
                          border: `1px solid ${isActive ? 'var(--color-primary-500)' : ADMIN_COLORS.border}`,
                        }}
                      >
                        {t(EXPORT_TEMPLATES[tmpl].labelKey)}
                      </button>
                    );
                  })}
                </div>

                {/* Columns — collapsible with count in header */}
                <div>
                  <button
                    type="button"
                    onClick={() => setColumnsExpanded(!columnsExpanded)}
                    className="flex items-center justify-between w-full mb-2"
                  >
                    <span
                      className="text-[10px] font-semibold uppercase tracking-wider"
                      style={{ color: ADMIN_COLORS.textSecondary }}
                    >
                      {t('export.wizard.columns')}
                      <span
                        className="ms-1.5 text-[10px] font-bold"
                        style={{ color: 'var(--color-primary-500)' }}
                      >
                        {selectedColumns.length}/{ALL_COLUMN_IDS.length}
                      </span>
                    </span>
                    <ChevronDown
                      className="w-3.5 h-3.5 transition-transform duration-200"
                      style={{
                        color: ADMIN_COLORS.textTertiary,
                        transform: columnsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      }}
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {columnsExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2, ease: 'easeInOut' }}
                        className="overflow-hidden"
                      >
                        <div className="grid grid-cols-2 gap-1 pb-2">
                          {ALL_COLUMN_IDS.map(colId => {
                            const isActive = selectedColumns.includes(colId);
                            return (
                              <label
                                key={colId}
                                className="flex items-center gap-2 rounded-lg px-2.5 py-2 cursor-pointer transition-colors hover:bg-white/60"
                                style={{
                                  backgroundColor: isActive ? 'rgba(255,255,255,0.8)' : 'transparent',
                                  border: `1px solid ${isActive ? 'var(--color-primary-200)' : 'transparent'}`,
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isActive}
                                  onChange={() => toggleColumn(colId)}
                                  className="neo-checkbox"
                                />
                                <span className="text-[11px] font-medium" style={{ color: ADMIN_COLORS.navy }}>
                                  {t(ALL_EXPORT_COLUMNS.find(c => c.id === colId)!.labelKey)}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Divider */}
                <div className="border-t" style={{ borderColor: ADMIN_COLORS.border }} />

                {/* Format radio cards */}
                <div className="grid grid-cols-3 gap-2">
                  {FORMAT_OPTIONS.map(opt => {
                    const Icon = opt.icon;
                    const isActive = format === opt.value;
                    return (
                      <button
                        key={opt.value}
                        onClick={() => setFormat(opt.value)}
                        className="flex flex-col items-center gap-2 rounded-xl px-3 py-4 transition-all"
                        style={{
                          backgroundColor: isActive ? opt.color : 'rgba(255,255,255,0.6)',
                          color: isActive ? '#fff' : ADMIN_COLORS.navy,
                          border: `1.5px solid ${isActive ? opt.color : ADMIN_COLORS.border}`,
                          boxShadow: isActive ? `0 2px 8px ${opt.color}33` : 'none',
                        }}
                      >
                        <Icon className="w-5 h-5" />
                        <span className="text-xs font-semibold">{t(opt.label)}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Layout options */}
                <div>
                  <div
                    className="text-[10px] font-semibold uppercase tracking-wider mb-2"
                    style={{ color: ADMIN_COLORS.textSecondary }}
                  >
                    {t('export.layout')}
                  </div>
                  <div className="space-y-1">
                    {getLayoutOptions(layoutPerItemKey).map(opt => {
                      const Icon = opt.icon;
                      const isActive = layout === opt.value;
                      return (
                        <button
                          key={opt.value}
                          onClick={() => setLayout(opt.value)}
                          className="flex items-center gap-3 w-full rounded-xl px-3 py-2.5 transition-all"
                          style={{
                            backgroundColor: isActive ? 'var(--color-primary-50)' : 'transparent',
                            border: `1.5px solid ${isActive ? 'var(--color-primary-300)' : ADMIN_COLORS.border}`,
                            color: isActive ? 'var(--color-primary-700)' : ADMIN_COLORS.navy,
                          }}
                        >
                          <Icon className="w-4 h-4" />
                          <span className="text-xs font-medium">{t(opt.label)}</span>
                          {isActive && (
                            <div
                              className="ms-auto w-2 h-2 rounded-full"
                              style={{ backgroundColor: 'var(--color-primary-500)' }}
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Save as Preset */}
                <div className="border-t pt-3" style={{ borderColor: ADMIN_COLORS.border }}>
                  {!showSavePreset ? (
                    <button
                      onClick={() => setShowSavePreset(true)}
                      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold transition-all hover:scale-[1.02] active:scale-[0.98]"
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.7)',
                        border: `1px solid ${ADMIN_COLORS.border}`,
                        color: ADMIN_COLORS.textSecondary,
                      }}
                    >
                      <Save className="w-3 h-3" />
                      {t('export.wizard.savePreset')}
                    </button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <input
                        value={presetName}
                        onChange={e => setPresetName(e.target.value)}
                        placeholder={t('export.wizard.presetName')}
                        className="flex-1 rounded-lg px-3 py-1.5 text-xs outline-none"
                        style={{
                          backgroundColor: 'rgba(241, 245, 249, 0.8)',
                          border: `1px solid ${ADMIN_COLORS.border}`,
                          color: ADMIN_COLORS.navy,
                        }}
                        onKeyDown={e => { if (e.key === 'Enter') handleSavePreset(); }}
                        autoFocus
                      />
                      <button
                        onClick={handleSavePreset}
                        disabled={!presetName.trim() || createPreset.isPending}
                        className="rounded-lg px-3 py-1.5 text-xs font-semibold text-white transition-all disabled:opacity-50"
                        style={{ backgroundColor: 'var(--color-primary-500)' }}
                      >
                        {createPreset.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : t('common.save')}
                      </button>
                      <button
                        onClick={() => { setShowSavePreset(false); setPresetName(''); }}
                        className="rounded-lg p-1.5 transition-colors hover:bg-slate-100"
                      >
                        <X className="w-3.5 h-3.5" style={{ color: ADMIN_COLORS.textSecondary }} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ───────── Step 4: Preview / Format & Layout ───────── */}
            {step === 4 && (
              hasPreview ? (
                <div className="space-y-3">
                  {/* Summary chips — click to jump back to that step */}
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => goToStep(1)}
                      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold transition-all hover:scale-[1.02] active:scale-[0.98]"
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.7)',
                        border: `1px solid ${ADMIN_COLORS.border}`,
                        color: ADMIN_COLORS.navy,
                      }}
                    >
                      <ItemIcon className="w-3 h-3" style={{ color: 'var(--color-primary-500)' }} />
                      {t('export.wizard.previewHospitals')} ({selectedEmployees.size})
                    </button>
                    <button
                      onClick={() => goToStep(2)}
                      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold transition-all hover:scale-[1.02] active:scale-[0.98]"
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.7)',
                        border: `1px solid ${ADMIN_COLORS.border}`,
                        color: ADMIN_COLORS.navy,
                      }}
                    >
                      <Filter className="w-3 h-3" style={{ color: '#059669' }} />
                      {t('export.wizard.previewFilters')} ({selectedStatuses.size})
                    </button>
                    <button
                      onClick={() => goToStep(3)}
                      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold transition-all hover:scale-[1.02] active:scale-[0.98]"
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.7)',
                        border: `1px solid ${ADMIN_COLORS.border}`,
                        color: ADMIN_COLORS.navy,
                      }}
                    >
                      <LayoutGrid className="w-3 h-3" style={{ color: '#dc2626' }} />
                      {t('export.wizard.previewReport')} ({selectedColumns.length} {t('export.wizard.columns').toLowerCase()}, {t(`export.wizard.format${format.charAt(0).toUpperCase() + format.slice(1)}` as any)})
                    </button>
                  </div>

                  {/* PDF preview — responsive height */}
                  <div
                    className="rounded-xl overflow-hidden"
                    style={{
                      border: `1px solid ${ADMIN_COLORS.border}`,
                      backgroundColor: 'rgba(241, 245, 249, 0.5)',
                    }}
                  >
                    <Suspense
                      fallback={
                        <div className="flex items-center justify-center py-16">
                          <Loader2 className="w-5 h-5 animate-spin" style={{ color: ADMIN_COLORS.primary }} />
                        </div>
                      }
                    >
                      <LazyPDFViewer width="100%" height={isMobile ? 400 : 500} showToolbar={false}>
                        {buildPreviewDocument!(wizardParams)}
                      </LazyPDFViewer>
                    </Suspense>
                  </div>
                </div>
              ) : (
                /* Fallback: Format & Layout for consumers without preview */
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-2">
                    {FORMAT_OPTIONS.map(opt => {
                      const Icon = opt.icon;
                      const isActive = format === opt.value;
                      return (
                        <button
                          key={opt.value}
                          onClick={() => setFormat(opt.value)}
                          className="flex flex-col items-center gap-2 rounded-xl px-3 py-4 transition-all"
                          style={{
                            backgroundColor: isActive ? opt.color : 'rgba(255,255,255,0.6)',
                            color: isActive ? '#fff' : ADMIN_COLORS.navy,
                            border: `1.5px solid ${isActive ? opt.color : ADMIN_COLORS.border}`,
                            boxShadow: isActive ? `0 2px 8px ${opt.color}33` : 'none',
                          }}
                        >
                          <Icon className="w-5 h-5" />
                          <span className="text-xs font-semibold">{t(opt.label)}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div>
                    <div
                      className="text-[10px] font-semibold uppercase tracking-wider mb-2"
                      style={{ color: ADMIN_COLORS.textSecondary }}
                    >
                      {t('export.layout')}
                    </div>
                    <div className="space-y-1">
                      {getLayoutOptions(layoutPerItemKey).map(opt => {
                        const Icon = opt.icon;
                        const isActive = layout === opt.value;
                        return (
                          <button
                            key={opt.value}
                            onClick={() => setLayout(opt.value)}
                            className="flex items-center gap-3 w-full rounded-xl px-3 py-2.5 transition-all"
                            style={{
                              backgroundColor: isActive ? 'var(--color-primary-50)' : 'transparent',
                              border: `1.5px solid ${isActive ? 'var(--color-primary-300)' : ADMIN_COLORS.border}`,
                              color: isActive ? 'var(--color-primary-700)' : ADMIN_COLORS.navy,
                            }}
                          >
                            <Icon className="w-4 h-4" />
                            <span className="text-xs font-medium">{t(opt.label)}</span>
                            {isActive && (
                              <div
                                className="ms-auto w-2 h-2 rounded-full"
                                style={{ backgroundColor: 'var(--color-primary-500)' }}
                              />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )
            )}
          </motion.div>
        </AnimatePresence>

        {/* Error */}
        {error && (
          <div
            className="mt-3 p-3 rounded-xl text-center text-xs font-bold"
            style={{
              backgroundColor: '#FEE2E2',
              border: `1px solid ${MODAL_COLORS.border}`,
              color: '#B91C1C',
            }}
          >
            {error}
          </div>
        )}
      </div>

      {/* Footer */}
      <div
        className="flex items-center gap-2 p-4 flex-shrink-0"
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          borderTop: `1px solid ${MODAL_COLORS.border}`,
          ...(isMobile
            ? {
                backdropFilter: 'blur(40px) saturate(150%)',
                WebkitBackdropFilter: 'blur(40px) saturate(150%)',
                paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))',
              }
            : {}),
        }}
      >
        {step > 1 ? (
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-1 rounded-xl px-4 py-2.5 text-xs font-semibold transition-all active:scale-[0.98]"
            style={{
              backgroundColor: 'rgba(241, 245, 249, 0.8)',
              border: `1px solid ${ADMIN_COLORS.border}`,
              color: ADMIN_COLORS.textSecondary,
            }}
          >
            <BackIcon className="w-3.5 h-3.5" />
            {t('export.wizard.back')}
          </button>
        ) : (
          <button
            onClick={handleClose}
            className="inline-flex items-center gap-1 rounded-xl px-4 py-2.5 text-xs font-semibold transition-all active:scale-[0.98]"
            style={{
              backgroundColor: 'rgba(241, 245, 249, 0.8)',
              border: `1px solid ${ADMIN_COLORS.border}`,
              color: ADMIN_COLORS.textSecondary,
            }}
          >
            {t('common.cancel')}
          </button>
        )}

        <div className="flex-1" />

        {step < TOTAL_STEPS ? (
          <button
            onClick={handleNext}
            disabled={!canNext()}
            className="inline-flex items-center gap-1 rounded-xl px-5 py-2.5 text-xs font-semibold text-white transition-all active:scale-[0.98] disabled:opacity-50"
            style={{
              backgroundColor: 'var(--color-primary-500)',
              boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
            }}
          >
            {t('export.wizard.next')}
            <NextIcon className="w-3.5 h-3.5" />
          </button>
        ) : (
          <button
            onClick={handleExport}
            disabled={isPending}
            className="inline-flex items-center gap-1.5 rounded-xl px-5 py-2.5 text-xs font-semibold text-white transition-all active:scale-[0.98] disabled:opacity-50"
            style={{
              backgroundColor: 'var(--color-primary-500)',
              boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
            }}
          >
            {isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <FileDown className="w-3.5 h-3.5" />
            )}
            {t('export.wizard.exportBtn')}
          </button>
        )}
      </div>
    </BaseModal>
  );
}
